import { buildCDNURL } from "@platformx/utilities";
import { omitEmpty } from "./common";

/**
 * Per-tenant publisher identity, resolved ONCE at site selection/login (see
 * `getGlobalDataWithHeader`) and cached here.
 *
 * The structured-data builders are synchronous pure functions called from
 * render effects and save handlers; fetching the site name/logo inside them
 * would make every one of them async and ripple across all their call sites.
 * They read this cache instead.
 */
export type PublisherIdentity = {
  name: string;
  logoUrl: string;
};

const PUBLISHER_IDENTITY_KEY = "sitePublisherIdentity";

export const readPublisherIdentity = (): PublisherIdentity | undefined => {
  if (typeof localStorage !== "object") return undefined;
  try {
    const stored = localStorage.getItem(PUBLISHER_IDENTITY_KEY);
    if (!stored) return undefined;
    const parsed = JSON.parse(stored);
    return parsed?.name ? { name: parsed.name, logoUrl: parsed.logoUrl || "" } : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Overwrite the cached identity. Called only with a value derived from the site
 * the session is switching INTO — writing a value resolved with the previous
 * tenant's headers would publish one customer's branding on another's pages.
 */
export const writePublisherIdentity = (identity?: PublisherIdentity): void => {
  if (typeof localStorage !== "object") return;
  // Fail closed: a site with no resolvable identity must clear the previous
  // site's value rather than inherit it.
  if (!identity?.name) {
    localStorage.removeItem(PUBLISHER_IDENTITY_KEY);
    return;
  }
  localStorage.setItem(PUBLISHER_IDENTITY_KEY, JSON.stringify(identity));
};

/**
 * The stored site logo comes back in two shapes: a legacy relative-path string
 * and the newer `original_image` object. Both are relative, so both need
 * `buildCDNURL` to become the absolute URL schema.org requires.
 */
export const resolveLogoUrl = (logo: any): string => {
  if (!logo) return "";
  if (typeof logo === "string") return buildCDNURL(logo);
  const { original_image_relative_path: relativePath = "", ext = "" } =
    logo?.original_image || logo || {};
  return relativePath ? buildCDNURL(relativePath, ext) : "";
};

/**
 * `Organization` node shared by every content type's markup, so the five
 * builders cannot drift from each other.
 *
 * `@type` is hardcoded `"Organization"`: the repo has no industry/vertical
 * concept to drive a narrower type such as `SportsOrganization`.
 * Returns `undefined` when no identity is cached, so callers omit `publisher`
 * rather than emitting a bare `{"@type": "Organization"}`.
 */
export const getPublisherNode = (): Record<string, any> | undefined => {
  const identity = readPublisherIdentity();
  if (!identity?.name) return undefined;
  return omitEmpty({
    "@type": "Organization",
    name: identity.name,
    logo: identity.logoUrl ? { "@type": "ImageObject", url: identity.logoUrl } : "",
  });
};
